import React, { useState, useEffect } from 'react';
import { User, Shield, UserPlus, AlertTriangle } from 'lucide-react';
import { supabase } from '../../utils/supabase';
import toast from 'react-hot-toast';

interface UserData {
  id: string;
  email: string;
  name: string | null;
  role: string;
  created_at: string;
}

export function UserManagement() {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [showAddAdmin, setShowAddAdmin] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const makeAdmin = async (userId: string, email: string) => {
    if (!confirm(`Are you sure you want to make ${email} an admin? This will grant them full access to all features and admin capabilities.`)) {
      return;
    }

    try {
      setLoading(true);

      // Update profile role
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ role: 'admin' })
        .eq('id', userId);

      if (profileError) throw profileError;

      // Create admin plan subscription
      const { error: subscriptionError } = await supabase
        .from('subscriptions')
        .insert({
          user_id: userId,
          plan_id: 'admin',
          status: 'active',
          current_period_end: '2099-12-31T23:59:59Z'
        });

      if (subscriptionError) throw subscriptionError;

      toast.success(`${email} is now an admin`);
      fetchUsers();
    } catch (error) {
      console.error('Error making user admin:', error);
      toast.error('Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const removeAdmin = async (userId: string, email: string) => {
    if (!confirm(`Are you sure you want to remove admin privileges from ${email}? They will lose access to admin features.`)) {
      return;
    }

    try {
      setLoading(true);

      // Update profile role
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ role: 'user' })
        .eq('id', userId);

      if (profileError) throw profileError;

      // Get free plan
      const { data: freePlan } = await supabase
        .from('plan_tiers')
        .select('id')
        .eq('price', 0)
        .eq('is_active', true)
        .single();

      if (!freePlan) throw new Error('No free plan available');

      // Update subscription to free plan
      const { error: subscriptionError } = await supabase
        .from('subscriptions')
        .update({
          plan_id: freePlan.id,
          status: 'active',
          current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        })
        .eq('user_id', userId);

      if (subscriptionError) throw subscriptionError;

      toast.success(`Removed admin privileges from ${email}`);
      fetchUsers();
    } catch (error) {
      console.error('Error removing admin:', error);
      toast.error('Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const addNewAdmin = async () => {
    if (!newEmail) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setLoading(true);

      // Check if email exists
      const { data: exists, error: checkError } = await supabase
        .rpc('check_email_exists', { p_email: newEmail });

      if (checkError) throw checkError;

      if (exists) {
        toast.error('A user with this email already exists');
        return;
      }

      // Create new user with admin role
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: newEmail,
        password: Math.random().toString(36).slice(-12), // Random password
        options: {
          data: {
            role: 'admin'
          }
        }
      });

      if (signUpError) throw signUpError;

      if (!authData.user) throw new Error('Failed to create user');

      toast.success('Admin user created successfully. They will receive an email to set their password.');
      setNewEmail('');
      setShowAddAdmin(false);
      fetchUsers();
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error('Failed to create admin user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card shadow rounded-lg p-6 border">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-foreground">User Management</h3>
        <button
          onClick={() => setShowAddAdmin(!showAddAdmin)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-primary-foreground bg-primary hover:bg-primary/90"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Admin
        </button>
      </div>

      {showAddAdmin && (
        <div className="mb-6 p-4 bg-secondary rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-3">Create New Admin User</h4>
          <div className="flex gap-3">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter email address"
              className="flex-1 rounded-md border-input bg-background text-foreground shadow-sm focus:border-primary focus:ring-primary"
            />
            <button
              onClick={addNewAdmin}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-primary-foreground bg-primary hover:bg-primary/90 disabled:opacity-50"
            >
              Create Admin
            </button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            The new admin will receive an email to set their password.
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-secondary">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Joined
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <User className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-foreground">{user.email}</div>
                      <div className="text-sm text-muted-foreground">{user.name || 'Not set'}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.role === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-secondary text-foreground'
                  }`}>
                    <Shield className="w-3 h-3 mr-1" />
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {user.role === 'admin' ? (
                    <button
                      onClick={() => removeAdmin(user.id, user.email)}
                      disabled={loading || user.email === 'admin@admin.com'}
                      className="text-destructive hover:text-destructive/80 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Remove Admin
                    </button>
                  ) : (
                    <button
                      onClick={() => makeAdmin(user.id, user.email)}
                      disabled={loading}
                      className="text-primary hover:text-primary/80"
                    >
                      Make Admin
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <div className="text-center py-6 text-muted-foreground">
          No users found
        </div>
      )}
    </div>
  );
}